import { ConsumableFormData } from '@/components/Operational/Inventory/Consumables/ConsumableForm';
import { getImgUrl } from '@/core/domain/archive/Archive/Archive.utils';
import { useUpdateConsumable } from '@/core/domain/operational/Consumables/Consumables.hooks';
import { UpdateConsumableRequest } from '@/core/domain/operational/Consumables/Consumables.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { Form } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';

type Props = PropsWithChildren<unknown>;

type Forms = {
  consumableGeneral?: ConsumableFormData;
};

type ConsumableFormContextProps = {
  isLoading: boolean;
  contextForm: Forms;
  consumableId: string;
  disabled: boolean;
  handleSubmit: () => void;
  active: boolean;
  setActive: (status: boolean) => void;
};

const ConsumableFormContext = createContext({} as ConsumableFormContextProps);
export const useConsumableFormContext = () => useContext(ConsumableFormContext);

const ConsumableFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { consumableId } = useRouteMatch<{ consumableId: string }>().params;

  const [contextForm, setContextForm] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [active, setActive] = useState<boolean>(true);

  const generalConsumableUpdate = useUpdateConsumable();

  const disabled = !Object.keys(contextForm).some((key) => !!key);

  useServiceError(generalConsumableUpdate.error);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setContextForm((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    try {
      if (contextForm.consumableGeneral) {
        const values = contextForm.consumableGeneral;
        const mutateObj: UpdateConsumableRequest = {
          ...values,
          id: +consumableId,
          active,
          sku: values.sku || undefined,
          imageUrl: values.imageUrl?.length
            ? getImgUrl(values.imageUrl[0])
            : undefined,
        };
        await generalConsumableUpdate.mutateAsync(mutateObj);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }
    setIsLoading(false);
    setContextForm({});
  }

  return (
    <ConsumableFormContext.Provider
      value={{
        isLoading,
        contextForm,
        consumableId,
        disabled,
        handleSubmit,
        active,
        setActive,
      }}
    >
      <Form.Provider
        onFormChange={handleFormChange}
        onFormFinish={handleSubmit}
      >
        {children}
      </Form.Provider>
    </ConsumableFormContext.Provider>
  );
};
export default ConsumableFormProvider;
